import { useState } from 'react';

import UploadFilesContainer from '~components/generic/elements/Upload/UploadFilesContainer';
import UploadInput, { UploadRejection } from '~components/generic/elements/Upload/UploadInput';
import { IEntityReference, IEntityReferenceSingle } from '~interfaces/api';
import operateOnItemOrArray from '~utils/operateOnItemOrArray';

export interface IUploadProps {
  label?: string;
  value: IEntityReferenceSingle | IEntityReferenceSingle[];
  onChange: (value: IEntityReference | IEntityReferenceSingle[]) => void;
  fieldName: string;
  onUpload: (payload: UploadFilesPayload) => void;
  theme?: 'dark' | 'light';
  hideUpload?: boolean;
  options?: {
    mimeTypes?: string[][];
  };
  multiple: boolean;
}

interface IUploadState {
  progress?: number;
}

export const Upload = ({
  label,
  value,
  onChange,
  onUpload,
  theme,
  hideUpload,
  options,
  multiple,
  progress,
}: IUploadProps & IUploadState) => {
  const [, setUploadProgress] = useState(progress);

  function handleUploadProgress(progress: number) {
    setUploadProgress(progress);
  }

  function handleRemove(entityId) {
    if (Array.isArray(value)) {
      const newValue = [...value].filter(ref => ref.id !== entityId);
      onChange(newValue);
      return;
    }
    onChange(null);
  }

  function handleUpload(files: File[]) {
    setUploadProgress(0);
    onUpload({
      files,
      progressCb: handleUploadProgress,
      completeCb: (ids: string[]) => {
        setUploadProgress(null);
        const files = operateOnItemOrArray(value, file => file, true) as IEntityReferenceSingle[];
        const newValue = (files ? [...files] : []).concat(ids.map(id => ({ id, type: 'file--file' })));
        onChange(newValue);
      },
    });
  }

  function handleUploadRejected(fileRejections: UploadRejection[]) {
    const errorMsg = 'The following files and/or file extensions are not allowed: \n';

    // get all extensions from rejected files (without duplicates)
    const extensions = fileRejections
      .map(fileRejection => fileRejection.file.name.split('.').pop())
      .filter((value, index, self) => self.indexOf(value) === index);

    alert(errorMsg + extensions.join(', '));
  }

  const fileIds = operateOnItemOrArray(value, ref => ref.id, true) as string[];

  return (
    <div>
      {label && <label className="c-label u-size-sm">{label}</label>}
      {fileIds &&
        fileIds.map(id => (
          <UploadFilesContainer key={`file_${id}`} entityId={id} onRemove={handleRemove} theme={theme} />
        ))}
      {!hideUpload && (
        <UploadInput
          theme={theme}
          progress={null}
          onUpload={handleUpload}
          multiple={multiple}
          options={options}
          onUploadRejected={handleUploadRejected}
        />
      )}
    </div>
  );
};

export default Upload;
